import React, { useEffect, useState } from 'react';
import {
  Box,
  Divider,
  Link,
  Flex,
  Skeleton,
  SkeletonCircle,
  Stack,
} from '@chakra-ui/react';
import { ArrowBackIcon } from '@chakra-ui/icons';
import {
  Link as ReactRouterLink,
  useParams,
  useLocation,
} from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import ActiveTransfer from '../components/ActiveTransfer';
import { TransactionsDetails, Text, Faqs } from '../components';
import { fetchTransfer } from '../api/accounts';
import { isEmpty } from '../utils/core';

const TransferDetails = () => {
  const location = useLocation();
  const { id: transferId } = useParams();
  const [error, setError] = useState(undefined);
  const [transfer, setTransfer] = useState({});
  const loadTransfer = async (transferId) => {
    try {
      const transfer = await fetchTransfer({ id: transferId }, true);
      setTransfer(transfer);
      setError(undefined);
    } catch (e) {
      setError(true);
      console.log('Failed to load transfer', e);
    }
  };
  useEffect(() => {
    loadTransfer(transferId);
  }, [transferId, error]);

  const { token: { tokenId, ercType, name, symbol, units: value } = {} } = transfer;
  const TransferDetailsSkeleton = () => (
    <Box>
      <Skeleton height="48px" width="25%" mb="48px" />
      <Box
        d="flex"
        flexDirection={['column', 'column', 'column', 'row']}
        justifyContent={['space-between', 'space-between', 'space-between', 'flex-start']}
      >
        <Box
          mb="32px"
          maxWidth="700px"
          boxShadow="lg"
          p="24px"
          width={['100%', '100%', '100%', '40%']}
        >
          <Flex grow={1} alignItems="center">
            <Skeleton width="48px" height="48px" mb="24px" />
            <Skeleton width="50%" height="36px" ml="16px" mb="24px" />
          </Flex>
          <Flex mt="16px">
            <Flex grow={1}>
              <SkeletonCircle size="10" mr="16px" />
              <Skeleton width="40%" />
            </Flex>
            <Flex grow={1}>
              <Skeleton width="60%" />
            </Flex>
          </Flex>
          <Flex mt="32px">
            <Flex grow={1}>
              <SkeletonCircle size="10" mr="16px" />
              <Skeleton width="40%" />
            </Flex>
            <Flex grow={1}>
              <Skeleton width="60%" />
            </Flex>
          </Flex>
          <Box paddingLeft="2px" paddingRight="2px" my="32px">
            <Skeleton height="1px" />
          </Box>
          <Box mt="32px">
            <SkeletonCircle size="10" mr="16px" />
            <Skeleton width="50%" height="36px" mt="24px" />
          </Box>
        </Box>
        <Stack p={['3px']} pl={[0, 0, 0, '64px']} maxWidth="700px" flex={1}>
          {Array(14).fill().map(() => (<Skeleton height="16px" key={uuidv4()} />))}
        </Stack>
      </Box>
      <Box width={['100%', '100%', '100%', '40%']} flex={1} maxWidth="700px">
        <Divider borderColor="grey5" mb="16px" />
        <Stack py="16px">
          {Array(20).fill().map(() => (<Skeleton height="16px" key={uuidv4()} />))}
        </Stack>
      </Box>
    </Box>
  );
  return (
    <Box>
      <Link
        as={ReactRouterLink}
        to={{
          pathname: '/',
          search: location.search,
        }}
        d="flex"
        alignItems="center"
        mb="20px"
      >
        <ArrowBackIcon ml="5px" fontSize="20px" _hover={{ fontWeight: 'bold' }} />
        <Text textStyle="bodyGrey">All transfers</Text>
      </Link>
      {error
        ? <Text textStyle="errorMessage">{`Error: could not load transfer '${transferId}'`}</Text>
        : isEmpty(transfer) ? <TransferDetailsSkeleton /> : (
          <>
            <Flex mb="60px">
              {!(value || parseInt(value, 10) > 1) ? null : (
                <Text d="flex" as="h1" textStyle="h1" mr="15px">{parseFloat(value).toString()}</Text>
              )}
              <Text
                textStyle="h1"
                as="h1"
                style={{ fontWeight: 'bold' }}
              >
                {`${name || symbol} ${ercType === 1155 ? `[${tokenId}]` : ''}`}
              </Text>
            </Flex>
            <ActiveTransfer
              transfer={transfer}
              transferOverviewProps={{ width: ['100%', '100%', '100%', '40%'] }}
              eventLogsStyle={{ flex: 1 }}
            />
            <Box width={['100%', '100%', '100%', '40%']} maxWidth="700px">
              {transfer.completed ? null : <Faqs mb="24px" />}
              {<Divider borderColor="grey5" mb="16px" />}
              {isEmpty(transfer.events) ? null : <TransactionsDetails transfer={transfer} />}
            </Box>
          </>
        )}
    </Box>
  );
};

export default TransferDetails;
